import React from 'react';

import { MarginProps } from '@opendoor/bricks/system/theme';

import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { ProgressBar, Chip } from '@opendoor/bricks/complex';

import {
  Icon,
  Box,
  Flex,
  Text,
  Card,
  Button,
  ButtonProps,
  CardListItem,
} from '@opendoor/bricks/core';

import { ExternalLink, InternalLink } from 'platform/react-router';
import { isOpendoorAppWebView } from 'shared/helpers/globals';
import { LocalDate } from '@js-joda/core';
import { MONTH_DAY_FORMAT } from 'shared/helpers/datetime';

const getLinkProps = (url: string) => {
  // if url is external link
  if (url.startsWith('http')) {
    return { type: 'external', target: '_blank', href: url };
  }
  // else the url is an internal route
  return {
    type: 'internal',
  };
};

export interface PresentationTaskCardProps {
  /** Title of the task to display. */
  title: string;
  /** Subtitle of the task to display. */
  subtitle?: string;
  /** Due text for the task. */
  due?: string;
  /** If the task is done. Shows a green tag if done. Defaults to false. */
  isDone?: boolean;
  /** Progress of the task completion, show a ProgressBar when progress is specified, range from 1 to 100. */
  progress?: number;
  /** If the task is disabled. Shows a non clickable Card if disabled. Default to false. */
  disabled?: boolean;
}

interface ButtonCardProps extends PresentationTaskCardProps {
  /** Callback to call when the task is clicked */
  onClick: Exclude<ButtonProps['onClick'], undefined>;
  /** Name used for analytics when the card is clicked */
  analyticsName: ButtonProps['analyticsName'];
}

interface LinkCardProps extends PresentationTaskCardProps {
  /** URL to navigate to when clicking the task */
  url: string;
  /** Name used for analytics when the card is clicked */
  analyticsName: string;
}

export type TaskCardProps = ButtonCardProps | LinkCardProps;

/**
 * Shared task card that renders a task. For example, tasks under a "To do" section on all tabs, including the Overview page.
 *
 * For more docs, see o/hub-tasks.
 *
 * component-test-check-ignore
 */
export const TaskCard: React.FC<TaskCardProps & MarginProps> = (props) => {
  if ('nonInteractive' in props || props.disabled) {
    return (
      <Box pb={isOpendoorAppWebView ? 5 : undefined}>
        <PresentationalTaskCard {...props} />
      </Box>
    );
  }

  if ('url' in props) {
    const linkProps = getLinkProps(props.url);
    if (linkProps.type === 'internal') {
      return (
        <InternalLink
          to={props.url}
          analyticsName={`task-${props.analyticsName}`}
          aria-label={null}
        >
          <PresentationalTaskCard {...props} />
        </InternalLink>
      );
    }

    return (
      <ExternalLink aria-label={null} analyticsName={`task-${props.analyticsName}`} {...linkProps}>
        <PresentationalTaskCard {...props} />
      </ExternalLink>
    );
  }

  if ('onClick' in props) {
    return (
      <Button
        analyticsName={`task-${props.analyticsName}`}
        onClick={props.onClick}
        aria-label={null}
        pb={isOpendoorAppWebView ? 5 : undefined}
        variant="ghost"
        width="100%"
      >
        <PresentationalTaskCard {...props} />
      </Button>
    );
  }

  return null;
};

/**
 * Shared task card that renders a task.
 * Design: https://www.figma.com/file/w3nEyANoniTZdjz8kCUaDS/Eero-Design-System?node-id=4620%3A14728.
 */
const PresentationalTaskCard: React.FC<PresentationTaskCardProps & MarginProps> = ({
  title,
  subtitle,
  isDone,
  disabled,
  due,
  progress,
  ...marginProps
}) => {
  if (disabled) {
    return (
      <Card
        variant="tertiary"
        borderWidth="2px"
        borderColor="neutrals30"
        p={[7, 7, 7]}
        color="neutrals90"
        height="100%"
      >
        <Flex width="100%" alignItems="center" justifyContent="space-between">
          <CardContent
            title={title}
            subtitle={subtitle}
            isDone={isDone}
            due={due}
            progress={progress}
            disabled={disabled}
          />
          <Icon name="chevron-right" size={16} color="neutrals90" />
        </Flex>
      </Card>
    );
  }
  return (
    <CardListItem
      as="div"
      color="neutrals100"
      variant="primary"
      cursor={disabled ? 'default' : 'pointer'}
      p={[7, 7, 7]}
      height="100%"
      {...marginProps}
    >
      <CardContent
        title={title}
        subtitle={subtitle}
        isDone={isDone}
        due={due}
        progress={progress}
      />
    </CardListItem>
  );
};

const CardContent: React.FC<PresentationTaskCardProps> = ({
  title,
  subtitle,
  isDone,
  due,
  progress,
  disabled,
}) => {
  const { isMobile } = useDevice();

  const progressBar = progress !== undefined && !isDone && (
    <Box width="100%" mt={4}>
      <ProgressBar progress={progress} gradientProgress={false} thickness={4} />
    </Box>
  );

  if (isMobile) {
    return (
      <Flex alignItems="start" flexDirection="column" width="100%" pr={4}>
        <Text fontSize="s1" fontWeight="semibold" color={disabled ? 'neutrals90' : 'neutrals100'}>
          {title}
          {subtitle && (
            <Text as="span" fontSize="s1" color="inherit">
              &nbsp;{subtitle}
            </Text>
          )}
        </Text>
        {progressBar}
        <TaskTag isDone={isDone} due={due} mt={4} />
      </Flex>
    );
  }
  return (
    <Flex flexDirection="column" width="100%" pr={4}>
      <Flex alignItems="center">
        <Text fontSize="s2" fontWeight="semibold" color={disabled ? 'neutrals90' : 'neutrals100'}>
          {title}
          {subtitle && (
            <Text as="span" ml={2} fontSize="s2" color="inherit">
              &nbsp;{subtitle}
            </Text>
          )}
        </Text>
        <TaskTag isDone={isDone} due={due} ml={6} />
      </Flex>
      {progressBar}
    </Flex>
  );
};

const TaskTag: React.FC<{ due?: string; isDone?: boolean } & MarginProps> = ({
  due,
  isDone,
  ...marginProps
}) => {
  if (isDone) {
    return (
      <Chip
        rightIcon={{ name: 'checkmark', color: 'neutrals0' }}
        borderColor="green50"
        backgroundColor="green50"
        text="Done"
        textColor="neutrals0"
        size="medium"
        {...marginProps}
      />
    );
  }
  if (due) {
    return (
      <Chip
        rightIcon={{ name: 'date-checked', color: 'neutrals90' }}
        backgroundColor="neutrals10"
        text={due}
        borderColor="neutrals30"
        textColor="neutrals90"
        size="medium"
        {...marginProps}
      />
    );
  }
  return null;
};

/** Formats a due date to a human-friendly string that will be shown in TaskCard. */
export const formatTaskCardDue = (date: LocalDate): string => {
  return `Due ${date.format(MONTH_DAY_FORMAT)}`;
};
